<script>
import pick from 'lodash/pick'
import isEmpty from 'lodash/isEmpty'
import keys from 'lodash/keys'
import {
  notificationComputed,
  notificationMethods,
} from '@state/helpers/notification'
import { agentDetailsMethods } from '@state/helpers/agent-details'
import {
  DELIVERED,
  DELIVERED_OPTIONS,
  NOTIFICATION_STATUS_NAME,
  NOTIFICATION_STATUS,
} from '@utils/constant'

import store from '@state/store'
import AgentDetail from './agent-detail.vue'

import deliveryTargetItems, {
  deliveryTargetFields,
} from './deliveryTargetItems'
export default {
  components: {
    AgentDetail,
  },
  props: {
    variant: {
      validator: function(value) {
        return !!~['add', 'edit'].indexOf(value)
      },
      default: 'add',
    },
    errors: {
      type: Object,
      default: () => {},
    },
    cms: {
      type: Boolean,
    },
    triggerMask: {
      type: Boolean,
      require: false,
    },
  },
  data() {
    return {
      deliveredOptions: DELIVERED_OPTIONS,
      delivered: DELIVERED.ON,
      items: deliveryTargetItems,
      fields: null,
      editable: false, // edit mode
    }
  },
  computed: {
    ...notificationComputed,
    add() {
      return this.variant === 'add'
    },
    deliveredComputed() {
      return this.delivered === DELIVERED.OFF
    },
    hiddenEditButton() {
      if (
        NOTIFICATION_STATUS[this.getNotificationDetail?.status] ===
        NOTIFICATION_STATUS[1]
      ) {
        return true
      }
      return (
        this.add ||
        (!this.add &&
          ![
            NOTIFICATION_STATUS_NAME.WAITING_FOR_DELIVERY,
            NOTIFICATION_STATUS_NAME.DELIVERY,
          ].includes(this.getNotificationDetail?.status))
      )
    },
  },
  watch: {
    fields() {
      this.$emit('change:data', this.fields)
    },
    delivered() {
      this.$emit('change:delivered', this.delivered)
    },
    getNotificationDetail() {
      if (this.getNotificationDetail) {
        this.syncNotificationData()
      }
    },
  },
  created() {
    this.init()
  },
  methods: {
    ...notificationMethods,
    ...agentDetailsMethods,
    async init() {
      if (this.add) {
        const currentUser = store.getters['auth/getCurrentUser']
        if (currentUser.agent.id) {
          await this.getDetailsById({
            id: currentUser.agent.id,
          })
        }
      } else {
        this.syncNotificationData()
        const notification = { ...this.getNotificationDetail }
        if (notification.agent_id) {
          await this.getDetailsById({
            id: notification.agent_id,
          })
        }
      }
    },
    syncNotificationData() {
      const detail = { ...this.getNotificationDetail }
      this.delivered = !isEmpty(detail?.filter_obj)
        ? DELIVERED.OFF
        : DELIVERED.ON
      if (this.delivered === DELIVERED.ON) {
        this.fields = null
      } else {
        this.fields = {
          ...pick(detail?.filter_obj, keys(deliveryTargetFields)),
        }
      }
    },
    handleChangeInputDeliveryTarget(field, value) {
      const fields = {}
      fields[field] = value
      this.fields = { ...this.fields, ...fields }
    },
    handleChangeDeliveryTarget(field, value) {
      const fields = {}
      fields[field] = value.join(',')
      this.fields = { ...this.fields, ...fields }
    },
    handleChangeRangeData(lower, upper, value) {
      const fields = {}
      if (typeof value.left === 'string') {
        const valueInt = parseInt(value.left)
        if (Math.abs(valueInt) >= 10000000000 || isNaN(valueInt)) {
          fields[lower] = value.left
        } else {
          fields[lower] = valueInt
        }
      } else {
        fields[lower] = value.left
      }
      if (typeof value.right === 'string') {
        const valueInt = parseInt(value.right)
        if (Math.abs(valueInt) >= 10000000000 || isNaN(valueInt)) {
          fields[upper] = value.right
        } else {
          fields[upper] = valueInt
        }
      } else {
        fields[upper] = value.right
      }
      this.fields = { ...this.fields, ...fields }
    },
    handleChangeRangeDate(lower, upper, value) {
      const fields = {}
      fields[lower] = value.date_start
      fields[upper] = value.date_end
      this.fields = { ...this.fields, ...fields }
    },
    toggleEditable() {
      this.editable = !this.editable
      this.syncNotificationData()
      this.$emit('enable:edit', this.editable)
    },
    handleCheckboxDeliveryTarget(value) {
      this.delivered = value
      if (value === DELIVERED.ON) {
        this.fields = null
      } else {
        this.fields = { ...deliveryTargetFields }
      }
    },
  },
}
</script>

<template>
  <div v-if="!cms" id="notification-delivery-target">
    <BaseCardDetail
      title="配信対象"
      form-collapsed
      :has-footer="!add && editable"
      :trigger-mask="!editable && triggerMask"
      @edit="toggleEditable"
      @cancel="toggleEditable"
      @submit="
        () => {
          toggleEditable()
          $emit('edit:notification')
        }
      "
    >
      <template v-if="hiddenEditButton" v-slot:header-action>
        <div />
      </template>
      <CRow>
        <CCol sm="3">
          全員配信
        </CCol>
        <CCol sm="9" class="d-flex align-items-baseline">
          <BaseRadioGroup
            :options="deliveredOptions"
            custom
            :disabled="!add && !editable"
            :checked="delivered"
            inline
            @update:checked="
              (v) => {
                handleCheckboxDeliveryTarget(v)
              }
            "
          />
          <label class="small">OFFの場合は、条件を指定してください</label>
        </CCol>
      </CRow>
      <CRow v-if="deliveredComputed">
        <CCol sm="3"> </CCol>
        <CCol sm="9">
          <div class="form-group">
            未入力または未選択の場合は絞り込み対象となりません
          </div>
        </CCol>
      </CRow>
      <div v-if="deliveredComputed">
        <div v-for="item in items" :key="item.key">
          <slot v-if="item.key === 'slot' && !add">
            <AgentDetail
              :disabled="!add && !editable"
              :fields="fields"
              @update:agent="(f, v) => handleChangeDeliveryTarget(f, v)"
              @update:agentInput="
                (f, v) => handleChangeInputDeliveryTarget(f, v)
              "
            />
          </slot>
          <CRow
            v-if="item.key !== 'slot'"
            class="d-flex align-items-baseline flex-wrap"
          >
            <CCol sm="3">
              <div class="col-form-label">
                {{ $t(`fields.${item.label}`) }}
              </div>
            </CCol>
            <CCol sm="9">
              <BaseCheckboxGroup
                v-if="item.as === 'checkboxGroup'"
                inline
                custom
                :options="item.options"
                :disabled="!add && !editable"
                :checked="(fields[item.key] || '').split(',')"
                @update:checked="(v) => handleChangeDeliveryTarget(item.key, v)"
              />
              <BaseRadioGroup
                v-if="item.as === 'radio'"
                inline
                custom
                :options="item.options"
                :disabled="!add && !editable"
                :checked.sync="fields[item.key]"
                @update:checked="(v) => handleChangeDeliveryTarget(item.key, v)"
              />
              <BaseDateRangerPicker
                v-if="item.as === 'dateRanger'"
                picker-type="datetime"
                :disabled="!add && !editable"
                :start-date="fields[item.lower]"
                :end-date="fields[item.upper]"
                :rules-start="{
                  requiredIfDate: {
                    target: fields[item.upper],
                  },
                  ...item.rulesStart,
                }"
                :rules-end="{
                  requiredIfDate: {
                    target: fields[item.lower],
                  },
                  ...item.rulesEnd,
                }"
                :rules="item.rules"
                @update:value="
                  (v) => handleChangeRangeDate(item.lower, item.upper, v)
                "
              />
              <BaseNumberInputRanger
                v-if="item.as === 'numberRanger'"
                :label="item.appendLabel"
                :left="fields[item.lower]"
                :right="fields[item.upper]"
                :plaintext="!add && !editable"
                :min="item.rules.min"
                :max="item.rules.max"
                :rules="item.rules"
                @update:value="
                  (v) => handleChangeRangeData(item.lower, item.upper, v)
                "
              />
            </CCol>
          </CRow>
        </div>
      </div>
    </BaseCardDetail>
  </div>
</template>

<style lang="scss" scoped>
#notification-delivery-target {
  ::v-deep .form-group {
    /* margin-bottom: 2rem; */
  }
}
</style>
